import DashboardLayout from '@/components/DashboardLayout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { ArrowLeft, AlertTriangle } from 'lucide-react'
import ShareFiscalRequestButton from '@/components/fiscal/ShareFiscalRequestButton'
import FiscalStatusBadges from '@/components/fiscal/FiscalStatusBadges'
import type { FiscalStatus } from '@/types/fiscal'

interface Mappable {
  mappable_type: 'lesson_package' | 'product'
  mappable_id: number
  name: string
  operational_active: boolean
  fiscal_status: FiscalStatus
  mapped: boolean
  conta_azul_item_id: string | null
  pending_sales_count: number
  requested_at: string | null
  linked_at: string | null
  share_subject: string
  share_body: string
}

interface ContaAzulItem {
  id: string
  name: string
}

interface FiscalMappingsProps {
  fiscal_enabled: boolean
  fiscal_document_scope: 'service_only' | 'service_and_product'
  includes_products: boolean
  conta_azul_connected: boolean
  conta_azul_services: ContaAzulItem[]
  conta_azul_products: ContaAzulItem[]
  services: Mappable[]
  products: Mappable[]
}

type RowState = Record<string, { conta_azul_item_id: string; saving: boolean }>

const keyFor = (m: Mappable) => `${m.mappable_type}:${m.mappable_id}`

export default function FiscalMappings({
  fiscal_enabled,
  includes_products,
  conta_azul_connected,
  conta_azul_services,
  conta_azul_products,
  services,
  products,
}: FiscalMappingsProps) {
  const [rows, setRows] = useState<RowState>(() => {
    const state: RowState = {}
    for (const m of [...services, ...products]) {
      state[keyFor(m)] = {
        conta_azul_item_id: m.conta_azul_item_id ?? '',
        saving: false,
      }
    }
    return state
  })
  const [onlyPending, setOnlyPending] = useState(false)

  const pendingCount = useMemo(
    () =>
      [...services, ...products].filter(
        (m) => m.fiscal_status !== 'homologated',
      ).length,
    [services, products],
  )
  const queuedSales = useMemo(
    () =>
      [...services, ...products].reduce(
        (sum, m) => sum + m.pending_sales_count,
        0,
      ),
    [services, products],
  )

  const setField = (m: Mappable, value: string) => {
    setRows((prev) => ({
      ...prev,
      [keyFor(m)]: { ...prev[keyFor(m)], conta_azul_item_id: value },
    }))
  }

  const handleSave = (m: Mappable) => {
    const row = rows[keyFor(m)]
    if (!row.conta_azul_item_id) {
      toast.error('Selecione o item correspondente no Conta Azul')
      return
    }

    setRows((prev) => ({
      ...prev,
      [keyFor(m)]: { ...prev[keyFor(m)], saving: true },
    }))
    router.patch(
      `/settings/fiscal_mappings/${m.mappable_type}/${m.mappable_id}`,
      { fiscal_mapping: { conta_azul_item_id: row.conta_azul_item_id } },
      {
        preserveScroll: true,
        onSuccess: () =>
          toast.success('Item homologado! Vendas represadas serão reenviadas.'),
        onError: (errors) =>
          toast.error(
            Object.values(errors).flat()[0] || 'Erro ao vincular o item',
          ),
        onFinish: () =>
          setRows((prev) => ({
            ...prev,
            [keyFor(m)]: { ...prev[keyFor(m)], saving: false },
          })),
      },
    )
  }

  const renderRow = (m: Mappable, catalog: ContaAzulItem[]) => {
    const row = rows[keyFor(m)]
    return (
      <div
        key={keyFor(m)}
        id={`${m.mappable_type}-${m.mappable_id}`}
        className="flex flex-col gap-3 rounded-md border p-4 md:flex-row md:items-end md:justify-between"
      >
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium">{m.name}</p>
          <FiscalStatusBadges
            operationalActive={m.operational_active}
            fiscalStatus={m.fiscal_status}
            pendingSalesCount={m.pending_sales_count}
          />

          <div className="max-w-md space-y-1 pt-1">
            <label className="text-xs text-muted-foreground">
              Cadastro no Conta Azul
            </label>
            <Select
              value={row.conta_azul_item_id}
              onValueChange={(value) => setField(m, value)}
              disabled={catalog.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    catalog.length === 0
                      ? 'Nenhum item disponível'
                      : 'Selecione o cadastro'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {catalog.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(m.requested_at || m.linked_at) && (
            <p className="text-xs text-muted-foreground">
              {m.requested_at && <>Solicitado em {m.requested_at}. </>}
              {m.linked_at && <>Homologado em {m.linked_at}.</>}
            </p>
          )}
        </div>

        <div className="flex shrink-0 gap-2">
          <ShareFiscalRequestButton
            mappableType={m.mappable_type}
            mappableId={m.mappable_id}
            subject={m.share_subject}
            body={m.share_body}
            pendingSalesCount={m.pending_sales_count}
          />
          <Button onClick={() => handleSave(m)} disabled={row.saving} size="sm">
            {row.saving ? 'Salvando...' : 'Vincular'}
          </Button>
        </div>
      </div>
    )
  }

  const visible = (list: Mappable[]) =>
    onlyPending ? list.filter((m) => m.fiscal_status !== 'homologated') : list

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => router.get('/settings')}
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Configurações
          </Button>
        </div>

        <div>
          <h2 className="text-2xl font-bold">Central Fiscal</h2>
          <p className="text-sm text-muted-foreground">
            Produtos e serviços são cadastrados e vendidos normalmente no
            Bewave. Aqui você só vincula cada item ao cadastro correspondente no
            Conta Azul — a parametrização fiscal (NCM, CFOP, ISS) é feita pelo
            BPO, no Conta Azul.
          </p>
        </div>

        {!fiscal_enabled && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              A emissão de nota fiscal está desativada. Ative em Configurações →
              Nota Fiscal para que os vínculos passem a valer.
            </span>
          </div>
        )}

        {fiscal_enabled && !conta_azul_connected && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              A Conta Azul não está conectada. Conecte em Configurações → Conta
              Azul para listar os cadastros disponíveis.
            </span>
          </div>
        )}

        {fiscal_enabled && queuedSales > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {queuedSales}{' '}
              {queuedSales === 1
                ? 'venda concluída está'
                : 'vendas concluídas estão'}{' '}
              aguardando o cadastro fiscal. Compartilhe a solicitação com o BPO
              — assim que o vínculo for feito, elas seguem sozinhas para o Conta
              Azul.
            </span>
          </div>
        )}

        {pendingCount > 0 && (
          <div className="flex items-center justify-between rounded-md border p-3">
            <p className="text-sm text-muted-foreground">
              {pendingCount}{' '}
              {pendingCount === 1 ? 'item pendente' : 'itens pendentes'} de
              homologação.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOnlyPending((v) => !v)}
            >
              {onlyPending ? 'Mostrar todos' : 'Mostrar só pendentes'}
            </Button>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Serviços (pacotes de aulas)
            </CardTitle>
            <CardDescription className="text-sm">
              Serviço do Conta Azul usado ao registrar a venda de cada pacote.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {visible(services).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {services.length === 0
                  ? 'Nenhum pacote de aula cadastrado.'
                  : 'Todos os serviços já estão homologados.'}
              </p>
            ) : (
              visible(services).map((m) => renderRow(m, conta_azul_services))
            )}
          </CardContent>
        </Card>

        {includes_products && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Produtos</CardTitle>
              <CardDescription className="text-sm">
                Produto do Conta Azul usado ao registrar a venda de cada item de
                estoque.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {visible(products).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {products.length === 0
                    ? 'Nenhum produto cadastrado.'
                    : 'Todos os produtos já estão homologados.'}
                </p>
              ) : (
                visible(products).map((m) => renderRow(m, conta_azul_products))
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  )
}
